import { useContext } from 'react';
import {
  InterfaceResourcesStoreContext,
  type InterfaceResourcesStoreContextValue,
} from '../../../../components/page/InterfacePageProviders/context/InterfaceResourcesStoreProvider';

interface InterfacePageResourcesFromSnapshot {
  /** Full ancestor snapshot, or null when this is a top-level preview. */
  interfaceResourcesSnapshot: InterfaceResourcesStoreContextValue | null;
  /** True when an ancestor preview has populated the snapshot — caller should read from it instead of fetching. */
  hasInterfaceResourcesSnapshot: boolean;
  /** This page's entity inside the ancestor snapshot, if found. */
  interfacePage: InterfaceResourcesStoreContextValue['interfacePages'][string];
  /** This interface's record inside the ancestor snapshot, if found. */
  interfaceRecord: InterfaceResourcesStoreContextValue['interfaceRecords'][string];
}

function useInterfaceResourcesStoreContext() {
  const context = useContext(InterfaceResourcesStoreContext);
  return context;
}

/**
 * Reads a parent saved-version preview's deep-traversal snapshot from context.
 *
 * The first preview to render fetches the entire embedded tree once and exposes it via
 * `InterfaceResourcesStoreContext`. Embedded modules rendered underneath call this hook
 * to grab their own page/record from that snapshot instead of firing duplicate network calls.
 *
 * Top-level previews (no ancestor) see a null snapshot and `hasParentSnapshot: false` —
 * the caller then owns the network fetch.
 */
export function useInterfacePageResourcesFromSnapshot({
  interfaceId,
  interfacePageId,
}: {
  interfaceId: string;
  interfacePageId: string | undefined;
}): InterfacePageResourcesFromSnapshot {
  const interfaceResourcesSnapshot = useInterfaceResourcesStoreContext();

  return {
    interfaceResourcesSnapshot,
    hasInterfaceResourcesSnapshot: interfaceResourcesSnapshot !== null,
    interfacePage: interfacePageId
      ? interfaceResourcesSnapshot?.interfacePages[interfacePageId]
      : undefined,
    interfaceRecord: interfaceResourcesSnapshot?.interfaceRecords[interfaceId],
  };
}
